/**
 * Traducciones de la tienda pública: barra superior, portada, filtros,
 * tarjetas de producto, llamado a la acción y pie.
 *
 * Este archivo es SUYO. Nadie más escribe aquí, para que dos personas
 * trabajando a la vez no se pisen el mismo mapa.
 *
 * ── Cómo se lee esto ─────────────────────────────────────────────────
 * La CLAVE es el texto en español; el valor, su inglés. El español no
 * necesita mapa: cuando falta la traducción, i18next devuelve la clave.
 *
 * Lo que NO va aquí: el contenido de los productos. Nombre, gancho y
 * descripción llegan traducidos del servidor y se resuelven con
 * `contentFor(product.translations, locale)` — lo escribe quien administra
 * el catálogo, no quien programa, y meterlo aquí obligaría a desplegar
 * para publicar un producto.
 *
 * `{{n}}` y `{{categoria}}` son interpolaciones: tienen que aparecer
 * IGUAL en el inglés o el número desaparece de la frase.
 */
export const shopEn: Record<string, string> = {
  // Ficha de producto (rediseño 28-ago)
  "Precio socio": "Member price",
  "ahorras {{amount}}": "you save {{amount}}",
  "Tu precio de socio": "Your member price",
  "Tu precio de franquiciatario": "Your franchisee price",
  "{{pct}}% menos que el de socio": "{{pct}}% below the member price",
  "{{n}} PV": "{{n}} PV",
  "Producto digital: no requiere envío.": "Digital product: no shipping needed.",
  "Envío a todo México y Estados Unidos.": "Shipping across Mexico and the United States.",
  "Capítulos de la ficha": "Product chapters",
  Mecanismo: "Mechanism",
  Comparativo: "Comparison",
  "Datos del producto": "Product facts",
  Comprar: "Buy",
  "Lo que dice la etiqueta": "What the label says",
  Porción: "Serving",
  "Porciones por envase": "Servings per container",
  Formato: "Format",
  "Fabricado en": "Made in",
  "Fabricado para": "Made for",
  "Uso sugerido": "Suggested use",
  Certificaciones: "Certifications",
  "Contraindicaciones y avisos": "Contraindications and warnings",
  "Te puede interesar": "You may also like",
  Relacionados: "Related",
  // ── Barra superior ────────────────────────────────────────────────
  Menú: "Menu",
  "Iniciar sesión": "Sign in",
  Regístrate: "Sign up",
  "Crear cuenta": "Create account",
  Moneda: "Currency",
  Idioma: "Language",
  "Precios en {{currency}}, según el país desde el que te conectas": "Prices in {{currency}}, by the country you are connecting from",
  "Carrito ({{n}} artículos)": "Cart ({{n}} items)",
  "Carrito vacío": "Empty cart",
  "Cerrar menú": "Close menu",
  "Menú de navegación": "Navigation menu",
  Navegación: "Navigation",
  Tienda: "Shop",
  Soporte: "Support",

  // ── Portada ───────────────────────────────────────────────────────
  //
  // El titular y la entradilla son los textos LITERALES de
  // `itsthebest1111.com`, no una redacción nueva: la portada de la tienda
  // tiene que sonar a la página principal. Por eso el inglés tampoco se
  // traduce palabra por palabra, sino que busca el mismo tono.
  //
  // «Bienvenido al» + «siguiente nivel.» llegan partidos porque la
  // segunda mitad va con el degradado de la marca. Es la única partición
  // de la pantalla, y se paga con que en inglés el resalte cae en otras
  // palabras; el resto de los textos viajan enteros.
  Catálogo: "Catalog",
  "Bienvenido al": "Welcome to the",
  "siguiente nivel.": "next level.",
  "Nutrición que llega a tu célula.": "Nutrition that reaches your cells.",
  "Más que una línea de suplementos: un estilo de vida enfocado en ti. No es solo un camino a la plenitud, sino el mejor.":
    "More than a line of supplements: a way of living built around you. Not just a path to feeling whole — the best one.",
  "Ver los productos": "See the products",

  // ── Filtros ───────────────────────────────────────────────────────
  "Filtrar por categoría": "Filter by category",
  Todos: "All",
  "Ordenar por": "Sort by",
  Recomendado: "Recommended",
  "Precio: de menor a mayor": "Price: low to high",
  "Precio: de mayor a menor": "Price: high to low",
  "Más recientes": "Newest",
  "{{n}} producto": "{{n}} product",
  "{{n}} productos": "{{n}} products",
  Productos: "Products",

  // ── Tarjetas y estados de la rejilla ──────────────────────────────
  Próximamente: "Coming soon",
  "Precio a consultar": "Price on request",
  "Cargando el catálogo…": "Loading the catalog…",
  'No hay productos en "{{categoria}}"': 'No products in "{{categoria}}"',
  "Todavía no hay productos publicados": "No products published yet",
  "Prueba con otra categoría o mira el catálogo completo.":
    "Try another category or browse the full catalog.",
  "Estamos preparando el catálogo. Vuelve pronto.":
    "We are preparing the catalog. Come back soon.",
  "Ver todos": "See all",
  "No pudimos cargar el catálogo": "We couldn't load the catalog",
  "Vuelve a intentarlo en un momento. Si el problema sigue, escríbenos.":
    "Try again in a moment. If the problem continues, write to us.",

  // ── Ficha de producto ─────────────────────────────────────────────
  "Volver al catálogo": "Back to the catalog",
  "Cargando el producto…": "Loading the product…",
  "No pudimos cargar el producto": "We couldn't load the product",
  "No encontramos este producto": "We couldn't find this product",
  "Puede que ya no esté disponible o que la dirección tenga un error.":
    "It may no longer be available, or the address may have a typo.",
  "Ver el catálogo": "See the catalog",
  "Imágenes del producto": "Product images",
  "Imagen {{n}} de {{producto}}": "Image {{n}} of {{producto}}",
  Beneficios: "Benefits",
  Categoría: "Category",

  /*
   * ── Los rótulos de los bloques editoriales ──────────────────────────
   * Solo el RÓTULO es de la interfaz. El título del bloque, su entradilla
   * y cada pilar, ingrediente o cifra vienen del servidor ya traducidos:
   * los escribe quien administra el catálogo, y meterlos aquí obligaría a
   * desplegar para publicar un producto.
   *
   * El número que se lee delante ("01 / Pilares") tampoco está aquí: se
   * calcula sobre los bloques que ese producto de verdad trae.
   */
  "Sobre el producto": "About the product",
  "De un vistazo": "At a glance",
  Pilares: "Pillars",
  Ingredientes: "Ingredients",
  "En cifras": "By the numbers",
  "En detalle": "In detail",
  Contenido: "Contents",
  Duración: "Duration",
  Toma: "Serving",
  Certificación: "Certification",

  Presentación: "Presentation",
  Existencias: "Stock",
  "{{n}} disponibles": "{{n}} available",
  "En existencia": "In stock",
  "Solo quedan {{n}}": "Only {{n}} left",
  Agotado: "Sold out",
  "Añadir al carrito": "Add to cart",
  "Descargar ficha técnica": "Download spec sheet",
  "Este producto no se vende en {{pedida}}. El precio se muestra en {{usada}}.":
    "This product isn't sold in {{pedida}}. The price is shown in {{usada}}.",

  // ── Carrito ───────────────────────────────────────────────────────
  "Cerrar el carrito": "Close the cart",
  "Artículos del carrito": "Cart items",
  "Cargando el carrito…": "Loading the cart…",
  "{{n}} artículo": "{{n}} item",
  "{{n}} artículos": "{{n}} items",
  "Tu carrito está vacío": "Your cart is empty",
  "Explora el catálogo y añade lo que quieras llevarte.":
    "Browse the catalog and add whatever you want to take home.",
  "Cantidad de {{producto}}": "Quantity of {{producto}}",
  "Añadir uno de {{producto}}": "Add one {{producto}}",
  "Quitar uno de {{producto}}": "Remove one {{producto}}",
  "Quitar {{producto}} del carrito": "Remove {{producto}} from the cart",
  "Este producto ya no está disponible": "This product is no longer available",
  "{{precio}} c/u": "{{precio}} each",
  Subtotal: "Subtotal",
  "El envío y los impuestos se calculan al finalizar la compra.":
    "Shipping and taxes are calculated at checkout.",
  "{{n}} artículos no se pueden sumar en {{moneda}}.":
    "{{n}} items can't be added up in {{moneda}}.",
  "Finalizar compra": "Checkout",
  "El pago en línea todavía no está disponible. Escríbenos para completar tu pedido.":
    "Online payment isn't available yet. Write to us to complete your order.",
  "Vaciar carrito": "Empty cart",

  // ── Bloque de cierre ──────────────────────────────────────────────
  //
  // El cierre de la tienda lleva a `/franquicias`, no a registro, y es
  // UNO solo: llegó a haber un segundo llamado hacia la franquicia
  // debajo, y al mover este al mismo destino sobraba.
  //
  // El titular viaja ENTERO en una clave. Partido en "¿Buscas producto
  // al por mayor" + "o quieres iniciar tu negocio" sería intraducible:
  // en inglés la disyuntiva y el nombre de la marca no caen en la misma
  // posición de la frase. `negocio virtual` es la clave del resalte, y
  // su inglés tiene que aparecer LITERALMENTE dentro del titular
  // traducido o `CtaHeading` pinta la frase entera sin resaltar.
  "Negocio digital": "Digital business",
  "¿Buscas producto al por mayor o quieres iniciar tu propio negocio virtual con It's The Best?":
    "Looking for wholesale product, or want to start your own online business with It's The Best?",
  "negocio virtual": "online business",
  "La franquicia digital es la puerta: te da tu propia tienda en línea, el catálogo completo y el acompañamiento de la comunidad.":
    "The digital franchise is the way in: your own online store, the full catalog and the community's support.",
  "Adquiere una franquicia digital": "Get a digital franchise",

  // ── Contacto ──────────────────────────────────────────────────────
  Contacto: "Contact",
  "¿Tienes preguntas?": "Have questions?",
  "Hablemos.": "Let's talk.",
  "Nuestro equipo está listo para ayudarte. Escríbenos y te respondemos a la brevedad.":
    "Our team is ready to help. Write to us and we'll get back to you shortly.",
  Correo: "Email",
  Comunidad: "Community",
  "Miembros en crecimiento": "A growing membership",
  Nombre: "Name",
  "Tu nombre": "Your name",
  Mensaje: "Message",
  "Escribe tu mensaje…": "Write your message…",
  "Enviar mensaje": "Send message",
  "Recibimos tu mensaje. Te responderemos muy pronto.":
    "We got your message. We'll reply very soon.",

  // ── Pie ───────────────────────────────────────────────────────────
  "Plataforma de bienestar con un modelo de venta directa sostenible. Productos premium, formación continua y la comunidad para hacer crecer tu negocio digital.":
    "A wellness platform with a sustainable direct-selling model. Premium products, continuous training and the community to grow your digital business.",
  "Únete al programa": "Join the program",
  /*
   * "Packages", no "Bundles".
   *
   * Las claves son globales aunque los mapas estén partidos por área, así
   * que esta traducción la heredan también el panel y la rejilla pública,
   * donde el mismo concepto se llamaba "package". Un sistema que nombra lo
   * mismo de dos formas enseña a desconfiar de sus propias etiquetas.
   */
  Paquetes: "Packages",
  Novedades: "New arrivals",
  "Mi carrito": "My cart",
  "Cuenta y soporte": "Account & support",
  "Recuperar contraseña": "Reset password",
  "Centro de soporte": "Support center",
  "WhatsApp · soporte": "WhatsApp · support",
  "Teléfono México": "Mexico phone",
  "Teléfono Estados Unidos": "United States phone",
  "Aviso de privacidad": "Privacy notice",
  "Términos y condiciones": "Terms and conditions",
  "Política de envío": "Shipping policy",
  Devoluciones: "Returns",
  "Todos los derechos reservados": "All rights reserved",
  "Suplementos alimenticios. Este producto no es un medicamento. Su consumo es responsabilidad de quien lo recomienda y de quien lo usa. Los testimonios son personales y no representan resultados garantizados.":
    "Food supplements. This product is not a medicine. Its use is the responsibility of whoever recommends it and whoever takes it. Testimonials are personal and do not represent guaranteed results.",

  // ── Tarjeta del catálogo ──────────────────────────────────────────
  //
  // "UDS" son unidades: la píldora de arriba a la derecha de la tarjeta,
  // el mismo dato y la misma abreviatura que muestra itsthebest1111.com.
  // En inglés se abrevia "UNITS" y no "UDS", que allí no significa nada.
  //
  // "Producto" es el rótulo de reserva del bloque inferior, para cuando
  // un producto no trae ni categoría ni gancho: el hueco vacío desplaza
  // el nombre hacia arriba y desalinea la fila entera de tarjetas.
  "{{n}} UDS": "{{n}} UNITS",
  "Ver producto": "View product",
  Producto: "Product",
  // ── Ficha de producto · bloques editoriales ───────────────────────
  // Van al FINAL a propósito: este archivo lo escriben dos personas y
  // añadir por abajo es lo único que no provoca un choque en la mitad.
  "Portada del producto": "Product cover",
  "Precio en {{usada}}": "Priced in {{usada}}",
  "Ficha técnica": "Specs",
  // Abreviado como en el original: la celda es una de cuatro y
  // "Certificación" la parte en dos renglones en el teléfono.
  "Cert.": "Cert.",
  "Dosis recomendada": "Recommended serving",
  "Mecanismo de acción": "How it works",
  Composición: "Composition",
  "Pareja conceptual": "Side by side",
  "Leyenda / Advertencias": "Legend / Warnings",

  /* ── Tienda personalizada, compra y páginas del sitio (1-sep) ── */
  "A la cuenta de la tienda": "To the store's account",
  "Abriendo la tienda…": "Opening the store…",
  "Adquiere un paquete y ahorra en esta compra y en las que siguen. Tu carrito se queda tal cual.": "Get a package and save on this order and the ones after it. Your cart stays exactly as it is.",
  "Ahora no": "Not now",
  "Al recibir tu pedido": "When your order arrives",
  "Av. Siempre Viva 742, Col. Centro": "742 Evergreen Terrace, Suite 3",
  "Banco": "Bank",
  "Bienvenido a": "Welcome to",
  "Buscar tu dirección": "Search for your address",
  "CLABE": "Account number",
  "Número de cuenta": "Account number",
  "Routing number (ACH)": "Routing number (ACH)",
  "Dirección del titular": "Account holder address",
  "Calle, número y colonia": "Street address",
  "Ciudad": "City",
  "Como aparece en tu identificación": "As it appears on your ID",
  "Comparte tu comprobante con {{name}}: al registrar tu pago, tu pedido cambia de estado.": "Share your receipt with {{name}}: once your payment is recorded, your order moves forward.",
  "Comprar solo este pedido": "Just buy this order",
  "Con tecnología de": "Powered by",
  "Confirmar pedido": "Confirm order",
  "Código postal": "ZIP code",
  "Efectivo": "Cash",
  "Elegir un paquete": "Choose a package",
  "Elige un paquete de It's The Best y quedas inscrito con el patrocinio de {{name}}.": "Choose an It's The Best package and you'll be enrolled under {{name}}.",
  "Emprende con {{name}}": "Start your business with {{name}}",
  "Esa tienda no existe": "That store doesn't exist",
  "Escríbeme para más información": "Message me for more information",
  "Ese producto no está en esta tienda": "That product isn't in this store",
  "Esta tienda está reabasteciendo": "This store is restocking",
  "Guarda tu folio. Total: {{total}}": "Keep your order number. Total: {{total}}",
  "Hola, {{name}}": "Hi, {{name}}",
  "Ir a It's The Best": "Go to It's The Best",
  "Ir a la tienda de It's The Best": "Go to the It's The Best store",
  "La tienda aún no registra su cuenta bancaria: pide los datos por su WhatsApp antes de transferir.": "This store hasn't added its bank details yet — ask for them on WhatsApp before you transfer.",
  "Método de pago": "Payment method",
  "No se pudo registrar tu pedido. Inténtalo otra vez.": "We couldn't place your order. Please try again.",
  "Pagarás en efectivo: coordina la entrega y el pago directamente con {{name}}.": "You'll pay in cash: arrange delivery and payment directly with {{name}}.",
  "Para pedir en {{name}} necesitas tu cuenta — así el pedido queda a tu nombre y puedes seguirlo.": "To order from {{name}} you need an account — that way the order is in your name and you can track it.",
  "Precio de {{name}} · {{n}} disponibles": "{{name}}'s price · {{n}} available",
  "Quién recibe (opcional)": "Who receives it (optional)",
  "Registrando…": "Placing your order…",
  "SKU": "SKU",
  "Salir": "Sign out",
  "Te pediremos entrar o crear tu cuenta; tu carrito te espera.": "We'll ask you to sign in or create an account; your cart will be waiting.",
  "Teléfono": "Phone",
  "Tienda de {{name}} · It's The Best": "{{name}}'s store · It's The Best",
  "Tienda personalizada de {{name}}": "{{name}}'s own store",
  "Titular": "Account holder",
  "Transferencia": "Bank transfer",
  "Transfiere a esta cuenta": "Transfer to this account",
  "Tu pedido en {{name}}": "Your order at {{name}}",
  "Volver a {{name}}": "Back to {{name}}",
  "Vuelve pronto, o visita la tienda de It's The Best.": "Check back soon, or visit the It's The Best store.",
  "devoluciones": "returns",
  "envios": "shipping",
  "privacidad": "privacy",
  "terminos": "terms",
  "¡Pedido registrado!": "Order placed!",
  "¿Dudas con un producto, un pedido o tu franquicia? Escríbenos.": "Questions about a product, an order or your franchise? Write to us.",
  "¿Quieres ahorrar hasta un 15% en tus compras?": "Want to save up to 15% on your orders?",
  "¿Quieres hasta un 15% de descuento?": "Want up to 15% off?",
  "Última actualización: 27 de agosto de 2026.": "Last updated: August 27, 2026.",
};
